/**
 * The screen an account sees when its password must be replaced.
 *
 * Shown instead of the portal, not inside it, and that is the whole design. The
 * API refuses every route except the one that changes the password, so a
 * sidebar rendered around this form would be a column of links that all return
 * 403 — a screen that looks like the application is broken rather than one that
 * says what to do.
 *
 * It reuses `ChangePasswordCard` rather than repeating the form. The two
 * situations differ in what is said around the fields, never in the fields.
 */

import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { ChangePasswordCard } from './ProfilePage';
import styles from './ProfilePage.module.css';

export function ForcedPasswordChange() {
  const { user, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  if (!user) return null;

  // Two reasons to be here, and they are not the same news. One is somebody
  // else's action; the other is the calendar.
  const expired = user.password.days_remaining !== null && user.password.days_remaining < 0;

  return (
    <main className={styles.forced}>
      <div className={styles.forcedInner}>
        <h1 className={styles.title}>
          {expired ? 'Your password has expired' : 'Your password was reset'}
        </h1>
        <p className={styles.forcedLead}>
          {expired
            ? 'Passwords are changed every 120 days.'
            : 'An administrator set this password.'}
        </p>

        <ChangePasswordCard forced />

        {/*
          A way out that is not the form. Somebody who reaches this on a shared
          machine, or who does not have their password to hand, needs an exit
          that is not closing the tab on a half-finished change.
        */}
        <button
          type="button"
          className={styles.forcedSignOut}
          disabled={signingOut}
          onClick={() => {
            setSigningOut(true);
            void signOut().finally(() => setSigningOut(false));
          }}
        >
          {signingOut ? 'Signing out…' : 'Sign out instead'}
        </button>
      </div>
    </main>
  );
}
